import {
  adminForceSignature,
  paymentTypes,
  transactionsTypes,
} from 'constants/campaignConstants';
import {
  campaignStatuses,
  campaignPaymentStatuses,
  billingStatusesItems,
} from 'constants/statuses';
import { venueNames, venueTypes } from 'constants/venueConstants';

import { parseArtDesignsRequest } from './campaignArtServices';

export const parseCampaign = (campaign) => {
  const {
    id,
    name,
    workflow_id: type,
    details,
    created_at: createdDate,
    corporate_profile_id: companyId,
    user_id: userId,
    workflow,
    status,
    billing_info_id: paymentMethodId,
    secondary_billing_info_id: secondaryPaymentMethodId,
    status_description: statusDescription,
    split_distribution: splitDistribution,
    transactions,
    signature,
    signed_at: signedAt,
    actual_start_date: actualStartDate,
    actual_end_date: actualEndDate,
    billing_status: billingStatus,
    art_change_requests: artChangeRequests,
    order_number: orderNumber,
  } = campaign;

  const isMonthlySubscription =
    details.payment_plan &&
    details.payment_plan.payment_plan_type ===
      paymentTypes.MONTHLY_SUBSCRIPTION;
  const initialAmount =
    isMonthlySubscription &&
    details.payment_plan &&
    +details.payment_plan.production_fee !==
      +details.payment_plan.initial_payment_due
      ? (
          +details.payment_plan.production_fee -
          +details.payment_plan.initial_payment_due
        ).toFixed(2)
      : null;

  const payment = details.payment_plan
    ? {
        initialPaymentDue: details.payment_plan.initial_payment_due,
        installmentPaymentAmount:
          details.payment_plan.installment_payment_amount,
        orderTotal: details.payment_plan.order_total,
        discount: details.payment_plan.discount,
        productionFee: details.payment_plan.production_fee,
        subtotal: details.payment_plan.subtotal,
        campaignDuration: details.payment_plan.campaign_duration,
        paymentType: details.payment_plan.payment_plan_type,
        countOfPayments: details.payment_plan.count_of_payments,
        costTotal: details.payment_plan.subtotal_before_discount,
        discountType: details.payment_plan.discount_type,
        discountNote: details.payment_plan.discount_note,
        initialAmount,
      }
    : {};

  const isEvergreenPayment = details?.payment_plan?.evergreen || false;

  const kiosks = details.kiosks.map((kiosk) => {
    return {
      // eslint-disable-next-line
      id: kiosk.id || kiosk._id,
      name: kiosk.name,
      image: kiosk.image,
      storeImage:
        venueNames[kiosk.name] ||
        venueTypes[kiosk.venue_type]?.image ||
        venueNames.default,
      storeId: kiosk.store_id,
      price: kiosk.price,
      address: kiosk.address,
      placementType: kiosk.placement_type,
      locationInVenue: kiosk.location_in_venue,
      displays: kiosk.displays,
      type: kiosk.type,
      storeAvailableKiosks: kiosk.store_available_kiosks,
      venueId: kiosk.venue_id,
      venueType: kiosk.venue_type,
      importKioskId: kiosk.import_kiosk_id,
      installedImageUrl: kiosk.installed_image_url,
      numberOfDigitalKiosks: kiosk.number_of_digital_kiosks,
      kioskIds: kiosk.kiosk_ids,
      isDigital: kiosk.is_digital,
      adDuration: kiosk.duration_in_seconds,
      rank: kiosk.rank,
      evergreenAmount: kiosk.evergreen_amount || 0,
    };
  });

  const statusHistory = details.status_history
    ? details.status_history
        .map(({ status, date }) => {
          return { status: status[0], date };
        })
        .filter(({ status }) => `${status}` !== campaignStatuses.DRAFT)
    : [];

  let paymentStatus = campaignPaymentStatuses.NOT_SUBMITTED;

  if (workflow?.payment_method?.completed) {
    paymentStatus = campaignPaymentStatuses.SUBMITTED;
  } else if (paymentMethodId) {
    paymentStatus = campaignPaymentStatuses.SIGNATURE_REQUIRED;
  }

  let startDate;
  if (status === campaignStatuses.BILLBOARD_INSTALLED && actualStartDate) {
    startDate = actualStartDate;
  }

  let endDate;

  if (status >= campaignStatuses.BILLBOARD_INSTALLED && actualEndDate) {
    endDate = actualEndDate;
  }

  const campaignTransactions = transactions
    ? transactions.map((el) => {
        const status =
          JSON.parse(el.details)?.nbResponse?.status_code === '1'
            ? 'SALE/OK'
            : 'SALE/FAILED';

        return {
          id: el.id,
          createdDate: el.created_at,
          chargeType: transactionsTypes[el.type],
          amount: `$${el.amount}`,
          status,
        };
      })
    : [];

  let signatureUrl = signature;

  if (signature) {
    if (signature.includes('http')) {
      signatureUrl = signature;
    } else if (signature === adminForceSignature) {
      signatureUrl = '/images/png/admin-signature.png';
    } else {
      signatureUrl = '/images/png/default.png';
    }
  }

  const isDraftCampaign = status === campaignStatuses.DRAFT;

  const renewedCampaignId = details.renewed_campaign_id;

  const parsedSplitDistribution = splitDistribution
    ? JSON.parse(splitDistribution)
    : {};

  return {
    id,
    userId,
    name,
    type,
    kiosks,
    payment,
    createdDate,
    startDate,
    continuityProgramCancellation: details.continiuty_program_cancellation,
    endDate,
    status,
    companyId,
    workflow,
    artPreferences: {},
    designArts: [],
    paymentStatus,
    paymentMethodId,
    secondaryPaymentMethodId,
    splitDistribution: {
      primaryBillingInfoPercentage:
        parsedSplitDistribution.primary_billing_info_percentage,
      secondaryBillingInfoPercentage:
        parsedSplitDistribution.secondary_billing_info_percentage,
    },
    statusHistory,
    statusDescription,
    transactions: campaignTransactions,
    signature: signatureUrl,
    signedAt,
    isDraftCampaign,
    renewedCampaignId,
    isEvergreenPayment,
    billingStatus,
    artChangeRequests: artChangeRequests
      ? artChangeRequests.map((el) => parseArtDesignsRequest(el))
      : [],
    orderNumber: orderNumber || '-',
  };
};

export const parseBilling = (billing) => {
  const descriptionInfo = {
    initial_payment_due: 'Initial',
    installment: 'Installment',
  };
  const typeInfo = {
    debit: 'Debit',
  };

  return {
    id: billing.id,
    description: descriptionInfo[billing.description],
    invoiceNumber: billing.invoice_number,
    scheduledDate: billing.schedule_date,
    billedDate: billing.actual_date,
    amount: billing.amount,
    financeFee: billing.finance_charge,
    total: billing.total,
    type: typeInfo[billing.charge_type],
    status: billingStatusesItems[billing.status] || {
      title: billing.status,
      value: billing.status,
      color: 'grey',
    },
  };
};
